import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {AuthenticationService} from '../../services/auth/authentication.service';
import {ApiService} from '../../services/api/api.service';
import {FormatService} from '../../services/format/format.service';
import * as moment from 'moment';

declare const $: any;

const CATEGORY_LABEL: any = {
  lack_of_verses: 'Kekurangan Ayat',
  excess_verses: 'Kelebihan Ayat',
  wrong_pronunciation: 'Salah Pengucapan',
};

@Component({
  selector: 'app-complaint',
  templateUrl: './complaint.component.html',
  styleUrls: ['./complaint.component.css']
})
export class ComplaintComponent implements OnInit, OnDestroy {

  isForm: any;
  isData: any = [];
  isExpanded: any = {}; // audio list per complaint id
  isTotalAll = 0;

  isKeyword = '';
  isCategory = '';
  isStatus = '';

  isPage = 1;
  isPageSize = 10;
  isPages: any = [];
  isTableLoading = false;

  attachment: any = null;
  dataSurah: any = [];

  isLoading = false;

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private authService: AuthenticationService,
    private apiService: ApiService,
    private formatService: FormatService,
  ) { }

  ngOnInit(): void {
    this.isPageSize = this.apiService.LIMIT || 10;
    this.getSurah();
    this.resetForm();
    this.getData();
  }

  ngOnDestroy(): void {
    if (this.player) { this.player.pause(); }
  }

  async resetForm() {
    this.attachment = null;
    this.isForm = this.fb.group({
      id: new FormControl(''),
      surah: new FormControl('', [Validators.required]),
      ayah: new FormControl('', [Validators.required, Validators.min(1)]),
      file: new FormControl(''),
    });
  }

  async getSurah() {
    try {
      this.apiService.methodGetSilent(`surah`, this.authService.getToken()).subscribe((response) => {
        if (response && response.status == 200) {
          this.dataSurah = response.data;
        }
      });
    } catch (error) {
      console.log(error);
    }
  }

  surahName(id: any) {
    const s = this.dataSurah.find((d: any) => d.id == id);
    return s ? s.text : `Surat ${id}`;
  }

  private searchTimer: any;
  private lastQuery = '';

  onKeywordInput(value: string) {
    this.isKeyword = value;
    clearTimeout(this.searchTimer);
    // debounce: wait 450ms after the last keystroke before hitting the API
    this.searchTimer = setTimeout(() => this.applyFilter(), 450);
  }

  async applyFilter() {
    clearTimeout(this.searchTimer);
    const query = `${this.isKeyword}|${this.isCategory}|${this.isStatus}`;
    if (query === this.lastQuery) { return; } // query unchanged, skip the request
    this.lastQuery = query;
    this.isPage = 1;
    this.isExpanded = {};
    await this.getData();
  }

  async getData() {
    const searchData = {keyword: this.isKeyword, category: this.isCategory, status: this.isStatus};
    const offset = (this.isPage - 1) * this.isPageSize;
    this.isTableLoading = true;
    try {
      this.apiService.methodGetSilent(`complaint/${offset}/${encodeURIComponent(JSON.stringify(searchData))}`, this.authService.getToken()).subscribe((response) => {
        this.isTableLoading = false;
        if (response && response.status == 200) {
          this.isData = [];
          (response.total ? response.data.forEach((element: any) => {
            // relations can be null — must never break the render
            element.userName = element.user ? element.user.name : '-';
            const begin = element.quran_begin_surah ? element.quran_begin_surah.name : `Surat ${element.begin_surah}`;
            const end = element.quran_end_surah ? element.quran_end_surah.name : `Surat ${element.end_surah}`;
            element.rangeVw = (element.begin_surah === element.end_surah)
              ? `${begin} ${element.begin_ayah}–${element.end_ayah}`
              : `${begin} ${element.begin_ayah} – ${end} ${element.end_ayah}`;
            element.categoryVw = CATEGORY_LABEL[element.category] || element.category || '-';
            element.createdVw = moment(element.createdAt).locale('id').format('D MMMM YYYY HH:mm');
            (element.audio || []).forEach((audio: any) => {
              audio.url = audio.file ? `${response.directoryFile}audio/${audio.file}` : '';
            });
            this.isData.push(element);
          }) : false);
          this.isTotalAll = response.total || 0;
          this.buildPager();
          // an active search auto-opens the rows — the match can sit in the notes
          if (this.isKeyword.trim()) {
            this.isData.forEach((v: any) => { this.isExpanded[v.id] = true; });
          }
        }
      }, () => { this.isTableLoading = false; });
    } catch (error) {
      this.isTableLoading = false;
      console.log(error);
    }
  }

  toggleRow(v: any) {
    this.isExpanded[v.id] = !this.isExpanded[v.id];
  }

  totalPages() {
    return Math.max(1, Math.ceil(this.isTotalAll / this.isPageSize));
  }
  buildPager() {
    const total = this.totalPages();
    const pages: any[] = [];
    for (let p = 1; p <= total; p++) {
      if (p == 1 || p == total || Math.abs(p - this.isPage) <= 1) {
        pages.push(p);
      } else if (pages[pages.length - 1] != '...') {
        pages.push('...');
      }
    }
    this.isPages = pages;
  }
  async goToPage(p: any) {
    const total = this.totalPages();
    if (p < 1 || p > total || p == this.isPage) { return; }
    this.isPage = p;
    await this.getData();
  }
  rowNo(i: number) {
    return (this.isPage - 1) * this.isPageSize + i + 1;
  }
  summaryFrom() {
    return this.isTotalAll ? (this.isPage - 1) * this.isPageSize + 1 : 0;
  }
  summaryTo() {
    return (this.isPage - 1) * this.isPageSize + this.isData.length;
  }
  formatNumber(value: any) {
    return this.formatService.dotFormat(value || 0);
  }

  // wrap keyword matches in <mark> for the list; input is HTML-escaped first
  highlight(text: any) {
    const esc = String(text == null ? '' : text)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const keyword = this.isKeyword.trim();
    if (!keyword) { return esc; }
    const escapedKeyword = keyword
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // neutralize regex metacharacters
    return esc.replace(new RegExp(`(${escapedKeyword})`, 'gi'), '<mark class="vl-hl">$1</mark>');
  }

  // ===== inline audio player: one shared element, play/pause toggles per row =====
  private player: any = null;
  playingId: any = 0;

  togglePlay(a: any) {
    if (!a.url) { return; }
    if (this.playingId === a.id) {
      this.player.pause();
      return; // the pause handler clears playingId
    }
    if (!this.player) {
      this.player = new Audio();
      this.player.onended = () => { this.playingId = 0; };
      this.player.onpause = () => { this.playingId = 0; };
    }
    this.player.src = a.url;
    const attempt = this.player.play();
    this.playingId = a.id;
    if (attempt && attempt.catch) {
      attempt.catch(() => {
        this.playingId = 0;
        this.toastr.error(`${a.file}`, `Gagal memutar audio`);
      });
    }
  }

  // ===== confirmations (status toggle / audio delete) =====
  isConfirm: any = null; // {mode: 'status', v, to} | {mode: 'audio-delete', v, a}
  isSaving = false;

  askStatus(v: any) {
    this.isConfirm = {
      mode: 'status',
      v: v,
      to: (v.status === 'approved') ? 'submitted' : 'approved',
    };
    document.body.style.overflow = 'hidden';
  }

  askDeleteAudio(v: any, a: any) {
    this.isConfirm = {mode: 'audio-delete', v: v, a: a};
    document.body.style.overflow = 'hidden';
  }

  closeConfirm() {
    if (this.isSaving) { return; } // never close while a request is in flight
    this.isConfirm = null;
    document.body.style.overflow = '';
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.isConfirm) { this.closeConfirm(); }
  }

  statusLabel(status: string) {
    return status === 'approved' ? 'Sudah Diperbaiki' : 'Belum Diperbaiki';
  }

  // reload without discarding filter/page; expands stay (row ids are stable)
  async refreshAfterMutation() {
    await this.getData();
    if (this.isData.length === 0 && this.isPage > 1) {
      this.isPage = this.isPage - 1;
      await this.getData();
    }
  }

  async confirmStatus() {
    const c = this.isConfirm;
    if (!c || this.isSaving) { return; }
    this.isSaving = true;
    this.apiService.methodPutSilent(`complaint/${c.v.id}`, {status: c.to}, this.authService.getToken()).subscribe(async (response) => {
      this.isSaving = false;
      if (response && response.status == 200) {
        this.closeConfirm();
        await this.toastr.success(`${c.v.rangeVw} — ${this.statusLabel(c.to)}`, `Status diubah`);
        this.refreshAfterMutation();
      } else {
        // failure: keep the dialog open so the user can retry
        await this.toastr.error((response && response.message) || `Coba lagi beberapa saat.`, `Gagal mengubah status`);
      }
    }, async () => {
      this.isSaving = false;
      await this.toastr.error(`Periksa koneksi lalu coba lagi.`, `Gagal mengubah status`);
    });
  }

  async confirmDeleteAudio() {
    const c = this.isConfirm;
    if (!c || this.isSaving) { return; }
    this.isSaving = true;
    this.apiService.methodDeleteSilent(`audio/${c.a.id}`, this.authService.getToken()).subscribe(async (response) => {
      this.isSaving = false;
      if (response && response.status == 200) {
        this.closeConfirm();
        await this.toastr.success(`${this.surahName(c.a.surah)} — Ayat ${c.a.ayah}`, `Audio dihapus`);
        this.refreshAfterMutation();
      } else {
        await this.toastr.error((response && response.message) || `Coba lagi beberapa saat.`, `Gagal menghapus`);
      }
    }, async () => {
      this.isSaving = false;
      await this.toastr.error(`Periksa koneksi lalu coba lagi.`, `Gagal menghapus`);
    });
  }

  // ===== audio fix dialog (edit only — complaints never create audio) =====
  isEditLabel = '';

  // show inline errors once a field is touched / after a submit attempt
  isInvalid(control: string) {
    const c = this.isForm.get(control);
    return c && c.invalid && (c.touched || c.dirty);
  }

  async onEdit(a: any) {
    try {
      await this.resetForm();
      this.isEditLabel = `${this.surahName(a.surah)} — Ayat ${a.ayah}`;
      await this.isForm.patchValue({id: a.id, surah: a.surah, ayah: a.ayah});
      // keyboard: false — Escape must never dismiss the dialog mid-save
      await $('#formData').modal({backdrop: 'static', keyboard: false, show: true});
    } catch (error) {
      console.log(error);
    }
  }

  async onFileSelect(e: any) {
    if (e.target.files.length > 0) {
      this.attachment = e.target.files[0];
    }
  }

  async onSubmit() {
    try {
      if (this.isForm.invalid) {
        this.isForm.markAllAsTouched();
        return;
      }
      if (this.isLoading) { return; }
      this.isLoading = true;
      const payload = this.isForm.getRawValue();
      const formValue = new FormData();
      formValue.append('id', payload.id);
      formValue.append('admin_id', this.authService.getSession().id);
      formValue.append('surah', payload.surah);
      formValue.append('ayah', payload.ayah);
      // the file lives in this.attachment — never in the form control (see playbook §8)
      if (this.attachment instanceof File) {
        formValue.append('file', this.attachment);
      }
      this.apiService.methodPutWithFormdata(`audio/${payload.id}`, formValue, this.authService.getToken()).subscribe(async (response) => {
        this.isLoading = false;
        if (response && response.status == 200) {
          // close the modal only on success — on failure the user's input stays intact
          $('#formData').modal('hide');
          await this.toastr.success(`${this.surahName(payload.surah)} — Ayat ${payload.ayah}`, `Audio diupdate`);
          await this.refreshAfterMutation(); // keep the user's page & filter
        } else {
          await this.toastr.error((response && response.message) || `Periksa isian lalu coba lagi.`, `Gagal menyimpan`);
        }
      }, async () => {
        this.isLoading = false;
        await this.toastr.error(`Periksa koneksi lalu coba lagi.`, `Gagal menyimpan`);
      });
    } catch (error) {
      this.isLoading = false;
      await this.toastr.error(`Periksa isian lalu coba lagi.`, `Gagal menyimpan`);
    }
  }
}
